import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { InstanceSystem, type InstanceVariant, type SubMesh } from './InstanceSystem';
import { createTreeMaterial } from './TreeMaterial';
import { commonGLSL, curvatureGLSL, lightingGLSL, terrainVisGLSL } from '../shaders/common';
import { shadowGLSL } from '../render/Shadows';
import { shared } from '../render/SharedUniforms';
import { terrainHeightGLSL } from './Terrain';
import type { Terrain } from './Terrain';
import type { TerrainData } from './TerrainData';
import type { ColliderGrid } from '../player/Controller';
import type { QualitySettings } from '../core/Quality';
import { Impostors, parseImpostorLayout } from './Impostors';

interface TreeInfo {
  species: string;
  height: number;
  crownRadius: number;
  trunkRadius: number;
  materials: string[];
}

const FOLIAGE = new Set(['foliage_pine', 'foliage_fir', 'foliage_juniper', 'foliage_limber', 'leaves_manzanita', 'foliage_sage', 'grass']);

function texPaths(mat: string): [string, string] {
  if (FOLIAGE.has(mat)) return [`${mat}.webp`, `${mat}_normal.webp`];
  return [`${mat}_albedo.webp`, `${mat}_normal.webp`];
}

export class Vegetation {
  meshes!: InstanceSystem;
  readonly group = new THREE.Group();
  readonly fade = { start: { value: 180 }, end: { value: 230 } };
  info!: Record<string, TreeInfo>;
  variantNames: string[] = [];
  nearRecords = new Float32Array(0);
  midRecords = new Float32Array(0);
  grass: THREE.Mesh | null = null;
  private grassUniforms: Record<string, THREE.IUniform> | null = null;
  impostors: Impostors | null = null;

  async load(base: string, terrainData: TerrainData, terrain: Terrain, colliders: ColliderGrid, shadowUniforms: Record<string, THREE.IUniform>,
    q: QualitySettings, maxAniso: number) {
    const [gltf, manifest, vegMeta, nearBuf, midBuf] = await Promise.all([
      new GLTFLoader().loadAsync(`${base}models/trees.glb`),
      fetch(`${base}models/trees.json`).then((r) => r.json()),
      fetch(`${base}data/vegetation.json`).then((r) => r.json()),
      fetch(`${base}data/veg_near.bin`).then((r) => r.arrayBuffer()),
      fetch(`${base}data/veg_mid.bin`).then((r) => r.arrayBuffer()),
    ]);
    const src = (manifest.trees ?? manifest) as Record<string, unknown>;
    this.info = Object.fromEntries(Object.entries(src).filter(([, v]) => v && typeof v === 'object' && 'height' in (v as object))) as Record<string, TreeInfo>;
    this.variantNames = vegMeta.variants;
    this.nearRecords = new Float32Array(nearBuf);
    this.midRecords = new Float32Array(midBuf);
    gltf.scene.updateMatrixWorld(true);

    // textures per material
    const tl = new THREE.TextureLoader();
    const il = new THREE.ImageLoader();
    const pendingTex: Promise<unknown>[] = [];
    const loadTex = (file: string, srgb: boolean) => {
      const t = new THREE.Texture();
      pendingTex.push(il.loadAsync(`${base}textures/vegetation/${file}`).then((img) => { t.image = img; t.needsUpdate = true; }).catch(() => undefined));
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.flipY = false;
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.anisotropy = Math.min(8, maxAniso);
      return t;
    };
    const matIndex = new Map<string, number>();
    const mats: THREE.Material[] = [];
    const depthMats: (THREE.Material | null)[] = [];
    const getMat = (name: string) => {
      let i = matIndex.get(name);
      if (i !== undefined) return i;
      const [a, n] = texPaths(name);
      const foliage = FOLIAGE.has(name);
      const { material, depthMaterial } = createTreeMaterial({
        map: loadTex(a, true), normal: loadTex(n, false), foliage,
        translucency: name === 'leaves_manzanita' ? 0.5 : foliage ? 0.42 : 0,
      }, shadowUniforms, this.fade);
      i = mats.length;
      mats.push(material);
      depthMats.push(depthMaterial);
      matIndex.set(name, i);
      return i;
    };

    // collect sub-meshes per (variant, lod)
    const subs = new Map<string, SubMesh[]>();
    gltf.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      let n: THREE.Object3D | null = mesh;
      let lodName = '';
      while (n) { if (/_LOD\d$/.test(n.name)) { lodName = n.name; break; } n = n.parent; }
      if (!lodName) return;
      const g = mesh.geometry.clone();
      const m = new THREE.Matrix4().extractRotation(mesh.matrixWorld);
      m.scale(new THREE.Vector3().setFromMatrixScale(mesh.matrixWorld));
      g.applyMatrix4(m);
      g.computeBoundingSphere();
      const matName = (mesh.material as THREE.Material).name;
      if (!subs.has(lodName)) subs.set(lodName, []);
      subs.get(lodName)!.push({ geometry: g, material: getMat(matName) });
    });
    const variants: InstanceVariant[] = this.variantNames.map((name) => {
      const lods: SubMesh[][] = [];
      for (let l = 0; l < 2; l++) { const s = subs.get(`${name}_LOD${l}`); if (s) lods.push(s); }
      const info = this.info[name];
      return { name, lods, radius: Math.max(info?.crownRadius ?? 3, (info?.height ?? 10) / 2) };
    });
    this.fade.start.value = q.treeMeshDistance * 0.82;
    this.fade.end.value = q.treeMeshDistance;
    this.meshes = new InstanceSystem(variants, mats, depthMats, {
      switchDist: [q.treeMeshDistance * 0.28],
      maxDist: q.treeMeshDistance,
      sizeRef: 8,
      shadowDist: 110,
    });
    this.meshes.shadowMaxCascade = 1;
    // instance data (near records only; mid forest is impostor-only)
    const N = this.nearRecords.length / 6;
    const i0 = new Float32Array(N * 4), i1 = new Float32Array(N * 4), vv = new Uint16Array(N);
    let k = 0;
    for (let i = 0; i < N; i++) {
      const r = this.nearRecords.subarray(i * 6, i * 6 + 6);
      if (q.treeDensity < 1 && ((i * 2654435761) >>> 0) / 4294967296 > q.treeDensity) continue;
      const x = r[0], z = r[2];
      const y = terrainData.heightAt(x, z) - 0.15;
      const s = r[4];
      i0.set([x, y, z, r[3]], k * 4);
      i1.set([s * (0.95 + 0.1 * ((i * 7919) % 13) / 13), s, s, ((i * 104729) % 1000) / 1000], k * 4);
      vv[k] = r[5];
      const info = this.info[this.variantNames[r[5]]];
      if (info && info.trunkRadius > 0.1 && info.height > 3) colliders.add({ x, z, r: info.trunkRadius * s + 0.15, y0: y - 1, y1: y + info.height * s * 0.6 });
      k++;
    }
    this.meshes.setInstances(i0.subarray(0, k * 4), i1.subarray(0, k * 4), vv.subarray(0, k));
    this.group.add(this.meshes.group);

    await Promise.all(pendingTex);
    if (q.grass) this.buildGrass(subs, terrain, shadowUniforms, getMat, mats);

    // impostors for distant trees (near records beyond mesh range + the mid-range forest)
    try {
      const layoutJson = await fetch(`${base}models/impostors.json`).then((r) => r.json());
      const layout = parseImpostorLayout(layoutJson);
      const ia = await tl.loadAsync(`${base}textures/vegetation/impostors_albedo.webp`);
      const inr = await tl.loadAsync(`${base}textures/vegetation/impostors_normal.webp`);
      for (const [t, srgb] of [[ia, true], [inr, false]] as [THREE.Texture, boolean][]) {
        t.flipY = false;
        t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        t.anisotropy = 4;
        t.needsUpdate = true;
      }
      this.impostors = new Impostors(layout, ia, inr, this.variantNames, [this.nearRecords, this.midRecords],
        (x, z) => terrainData.heightAt(x, z), q.treeDensity, shadowUniforms, this.fade, q.impostorDistance);
      this.group.add(this.impostors.mesh);
    } catch (e) {
      console.warn('tree impostors unavailable', e);
    }
  }

  private buildGrass(subs: Map<string, SubMesh[]>, terrain: Terrain, shadowUniforms: Record<string, THREE.IUniform>,
    getMat: (n: string) => number, mats: THREE.Material[]) {
    const src = subs.get('grass_0_LOD0')?.[0] ?? subs.get('grass_1_LOD0')?.[0];
    if (!src) return;
    const R = 34, cell = 1.05;
    const n = Math.ceil(R / cell);
    const cells: number[] = [];
    for (let i = -n; i <= n; i++) for (let j = -n; j <= n; j++) if (i * i + j * j <= n * n) cells.push(i, j);
    const g = new THREE.InstancedBufferGeometry();
    g.index = src.geometry.index;
    for (const [k, a] of Object.entries(src.geometry.attributes)) g.setAttribute(k, a);
    g.setAttribute('aCell', new THREE.InstancedBufferAttribute(new Float32Array(cells), 2));
    g.instanceCount = cells.length / 2;
    const tu = terrain.material.uniforms;
    const gm = mats[getMat('grass')] as THREE.ShaderMaterial;
    this.grassUniforms = {
      ...shared, ...shadowUniforms,
      uH0: tu.uH0, uH1: tu.uH1, uH2: tu.uH2, uHB0: tu.uHB0, uHB1: tu.uHB1, uHB2: tu.uHB2, uHM: tu.uHM,
      uA0: tu.uN0, uTrail: tu.uTrail,
      uMap: gm.uniforms.uMap, uNormalMap: gm.uniforms.uNormalMap,
      uCell: { value: cell }, uRadius: { value: R },
    };
    const vert = /* glsl */ `
      uniform vec4 uWind;
      uniform float uTime;
      uniform vec3 uCamPos;
      uniform float uSnowCover;
      uniform sampler2D uA0;
      uniform sampler2D uTrail;
      uniform float uCell;
      uniform float uRadius;
      in vec2 aCell;
      in vec4 color;
      out vec3 vWorld;
      out vec3 vNormal;
      out vec2 vUv;
      out float vAO;
      void main() {
        vec2 base = floor(uCamPos.xz / uCell) * uCell;
        vec2 cw = base + aCell * uCell;
        vec2 jit = hash22(cw * 0.731 + 3.1);
        vec2 p = cw + jit * uCell;
        vec2 uv0 = (p - uHB0.xy) / uHB0.z;
        float veg = texture(uA0, uv0).a;
        float h = terrainHeight(p);
        float hx = terrainHeight(p + vec2(0.7, 0.0)) - h, hz = terrainHeight(p + vec2(0.0, 0.7)) - h;
        float slope = length(vec2(hx, hz)) / 0.7;
        vec4 tr = texture(uTrail, uv0);
        float onTrail = 1.0 - smoothstep(0.9, 1.6, min(tr.r, tr.g) * 12.0);
        float roads = 1.0 - smoothstep(3.5, 5.0, tr.b * 24.0);
        float plateau = smoothstep(2395.0, 2425.0, h);
        float dens = (0.12 + 0.55 * plateau + 0.4 * smoothstep(0.1, 0.5, veg)) * (1.0 - smoothstep(0.45, 0.8, slope)) * (1.0 - onTrail) * (1.0 - roads) * (1.0 - uSnowCover * 0.9);
        float d = distance(p, uCamPos.xz);
        float fade = 1.0 - smoothstep(uRadius * 0.6, uRadius, d);
        float keep = step(hash12(p * 1.37), dens) * fade;
        float rot = hash12(p * 2.11) * 6.2831;
        float sc = (0.65 + 0.7 * hash12(p * 5.3)) * keep;
        float c = cos(rot), s = sin(rot);
        mat3 R = mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c);
        vec3 lp = position * sc;
        vec3 wp = R * lp + vec3(p.x, h - 0.05, p.y);
        float w = color.g;
        wp.xz += uWind.xy * (0.02 + 0.012 * uWind.z) * w * w * sin(uTime * 2.1 + p.x * 0.5 + p.y * 0.3) * sc;
        vWorld = wp;
        vNormal = normalize(R * normal + vec3(0.0, 0.6, 0.0));
        vUv = uv;
        vAO = color.r;
        wp.y -= curvatureDrop(wp);
        gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
      }`;
    const frag = /* glsl */ `
      uniform sampler2D uMap;
      in vec3 vWorld;
      in vec3 vNormal;
      in vec2 vUv;
      in float vAO;
      layout(location = 0) out vec4 fragColor;
      void main() {
        vec4 t = texture(uMap, vUv);
        if (t.a < 0.3) discard;
        vec3 N = normalize(vNormal);
        if (!gl_FrontFacing) N = -N;
        vec3 alb = t.rgb * vec3(1.05, 0.98, 0.85);
        alb = mix(alb, vec3(0.88, 0.9, 0.93), uSnowCover * 0.7);
        vec3 V = normalize(cameraPosition - vWorld);
        float sh = getShadow(vWorld, vec3(0.0, 1.0, 0.0), gl_FragCoord.xy);
        vec3 col = shadeSurface(alb, N, V, vWorld, 0.7, 0.03, sh, 0.9 * terrainSkyVis(vWorld), mix(0.5, 1.0, vAO), vec3(0.45, 0.3, 0.2), 0.4);
        fragColor = vec4(col, t.a);
      }`;
    const mat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: commonGLSL + curvatureGLSL + terrainHeightGLSL + vert,
      fragmentShader: commonGLSL + lightingGLSL + shadowGLSL + terrainVisGLSL + frag,
      uniforms: this.grassUniforms,
      side: THREE.DoubleSide,
      alphaToCoverage: true,
    });
    this.grass = new THREE.Mesh(g, mat);
    this.grass.frustumCulled = false;
    this.group.add(this.grass);
  }

  update(camera: THREE.PerspectiveCamera) {
    this.meshes?.update(camera);
    this.impostors?.update(camera);
  }
}
